import {
  BossFight, EventModesData, EventModesState, EventModesStateJson, EventRunMode, Fix64, GearItem, LevelModifiers, LiveEventData, MEDALS, Medal, ModePrize, OdysseyNext,
  OdysseyWeekData, PaidReward, RaceRoad, bossFight, eventAt, eventModeOf, eventsUnlocked, isoWeek, metaSeed, mondayOf, odysseyDeck, odysseyMap, odysseyWeek,
  payReward, raceMedal, raceRoad, rotationWeek,
} from "@highmarch/domain";
import { HasSections, registerSaveSection } from "./saveSections";
import { GameEventHub, gameEvents } from "./gameEvents";
import { playerProfile } from "./runProfile";
import type { LevelCatalog } from "./levelCatalog";

/**
 * The weekly event modes (MP-21, docs/MODES.md §5) as use cases: which mode this week runs, the run each of its
 * buttons starts (Boss Week's tiers, Race Week's road, the Odyssey's next map), what a finished run pays — each prize
 * once a week, a reward plus embers to the week's track — and Race Week's announcement (`raceFinished`). The state
 * is the `eventModes` save section (domain EventModesState), bound by the feature that owns it.
 */

function asObject<T>(v: unknown): T | undefined {
  return typeof v === "object" && v !== null && !Array.isArray(v) ? (v as T) : undefined;
}

export const eventModesSection = registerSaveSection<EventModesStateJson>("eventModes", asObject, (v) => v);

export function eventModesFromSave(save: HasSections): EventModesState { return EventModesState.fromJson(eventModesSection.read(save)); }

/** Seed tag of a mode prize's chest (with the week's event id and the prize's number). */
const PRIZE_TAG = "eventMode";
/** A prize's number in its seed: Boss tiers 1–6, medals 11–13, Odyssey maps 21–23 and the full Odyssey 29. */
const MEDAL_BASE = 11, MAP_BASE = 21, FULL_PRIZE = 29;

/** The live week's mode. */
export interface ModeWeek { readonly event: LiveEventData; readonly mode: EventRunMode; readonly monday: number }

/** A run to start: the layout and its modifiers. */
export interface EventRun { readonly level: number; readonly modifiers: LevelModifiers }

export interface BossTierView { readonly tier: number; readonly cleared: boolean; readonly open: boolean; readonly fight: BossFight }
export interface RaceView { readonly road: RaceRoad; readonly best: number; readonly medal: Medal | ""; readonly paid: readonly Medal[]; readonly runs: number }
export interface OdysseyView {
  readonly week: OdysseyWeekData;
  /** The deck the runs deal (tower indexes; a tower the player lacks replaced). */
  readonly deck: readonly number[];
  readonly levels: readonly number[];
  readonly cleared: readonly number[];
  readonly next: OdysseyNext | null;
  readonly inProgress: boolean;
  readonly attempts: number;
  readonly attemptsLeft: number;
  /** The life pool now: the running attempt's, else a full Fortress. */
  readonly lives: number;
  readonly maxLives: number;
  readonly full: boolean;
}

/** A finished event run as the end screen reports it. `key` identifies the run (it counts once). */
export interface FinishedEventRun {
  readonly modifiers: LevelModifiers;
  readonly level: number;
  readonly won: boolean;
  /** Clear time in sim milliseconds (LevelSim.clearMs). */
  readonly ms: number;
  /** Fortress HP left. */
  readonly baseHp: number;
  readonly key: string;
  readonly now: number;
}

/** One prize a run paid. */
export interface PrizePaid { readonly label: string; readonly paid: PaidReward; readonly gear: readonly GearItem[]; readonly embers: number }

export interface EventModeOutcome {
  readonly mode: EventRunMode;
  readonly step: number;
  readonly won: boolean;
  readonly counted: boolean;
  readonly prizes: readonly PrizePaid[];
  readonly embers: number;
  readonly event: LiveEventData | null;
  /** Race Week: the time, its medal, the week's best and whether this beat it. */
  readonly ms: number;
  readonly medal: Medal | "";
  readonly best: number;
  readonly newBest: boolean;
  /** Odyssey: the attempt ended (lost, or all three maps cleared), and the life pool left. */
  readonly over: boolean;
  readonly lives: number;
}

const MEDAL_LABEL: Readonly<Record<Medal, string>> = { bronze: "Bronze medal", silver: "Silver medal", gold: "Gold medal" };

export class EventModes {
  constructor(private readonly catalog: LevelCatalog, readonly state: EventModesState, private readonly events: GameEventHub = gameEvents) {}

  get data(): EventModesData | null { return this.catalog.balance.events.modes ?? null; }

  /** This week's mode while it runs (and events are open), its first look locking the week's anchor. */
  week(now: number): ModeWeek | null {
    const ev = this.catalog.balance.events, e = eventAt(ev, now), mode = eventModeOf(e);
    if (!e || !mode || !this.data || !eventsUnlocked(ev, this.catalog.highestLevelCleared)) return null;
    const monday = mondayOf(e.startDay);
    this.state.enter(monday, this.catalog.highestLevelCleared);
    return { event: e, mode, monday };
  }

  private anchor(): number { return this.state.anchor; }

  // ---------------------------------------------------------------- Boss Week
  bossTiers(now: number): BossTierView[] {
    const w = this.week(now);
    if (!w || w.mode !== "boss") return [];
    return this.data!.boss.tiers.map((_, i) => ({ tier: i + 1, cleared: this.state.bossCleared.includes(i + 1), open: this.state.bossOpen(i + 1), fight: bossFight(this.catalog.balance, w.monday, i + 1, this.anchor()) }));
  }
  bossRun(now: number, tier: number): EventRun | null {
    const t = this.bossTiers(now).find((x) => x.tier === tier);
    return t && t.open ? { level: t.fight.level, modifiers: t.fight.modifiers } : null;
  }

  // ---------------------------------------------------------------- Race Week
  race(now: number): RaceView | null {
    const w = this.week(now);
    if (!w || w.mode !== "race") return null;
    const road = raceRoad(this.catalog.balance, w.monday, this.anchor()), best = this.state.raceBest;
    return { road, best, medal: best > 0 ? raceMedal(road.road, best) : "", paid: this.state.medalsPaid, runs: this.state.raceRuns };
  }
  raceRun(now: number): EventRun | null { const r = this.race(now); return r ? { level: r.road.level, modifiers: r.road.modifiers } : null; }

  // ---------------------------------------------------------------- Odyssey
  /** The Fortress's full HP for the player (gear and VIP included), as LevelSim.maxBaseHp counts it. */
  maxLives(): number {
    const B = this.catalog.balance, base = B.towers.findIndex((t) => t.isBase), gear = playerProfile(this.catalog).gear[base];
    return Fix64.fromInt(B.economy.baseHp).mul(Fix64.One.add(gear?.hp ?? Fix64.Zero)).floorToInt();
  }

  odyssey(now: number): OdysseyView | null {
    const w = this.week(now), d = this.data;
    if (!w || w.mode !== "odyssey" || !d) return null;
    const week = odysseyWeek(this.catalog.balance.events, w.monday), next = this.state.odysseyNext(d.odyssey.attempts), max = this.maxLives();
    return {
      week, deck: odysseyDeck(this.catalog.balance, week.deck, this.catalog.ownedTowers()), levels: week.maps.map((l) => Math.min(l, this.catalog.levelCount)),
      cleared: this.state.odysseyCleared, next, inProgress: this.state.odysseyInProgress, attempts: d.odyssey.attempts, attemptsLeft: this.state.attemptsLeft(d.odyssey.attempts),
      lives: this.state.odysseyLive && this.state.odysseyLives > 0 ? this.state.odysseyLives : max, maxLives: max, full: this.state.odysseyFull,
    };
  }
  odysseyRun(now: number): EventRun | null {
    const v = this.odyssey(now), w = this.week(now);
    if (!v || !v.next || !w) return null;
    return odysseyMap(this.catalog.balance, w.monday, v.next.map, v.next.lives, this.anchor(), this.catalog.ownedTowers());
  }

  /** The suspended Odyssey map is gone (abandoned): the attempt it was part of is over. */
  abandoned(): void { this.state.abandonOdyssey(); }

  /** A fresh event run started (the level flow; never a resumed one): an Odyssey map begins (map 1 opens an attempt). */
  beginRun(modifiers: LevelModifiers): void {
    const e = modifiers.event, d = this.data;
    if (e?.mode === "odyssey" && d) this.state.beginOdyssey(e.week, e.step, d.odyssey.attempts);
  }

  /**
   * The run the end screen's next button starts after `modifiers`: Boss Week's next tier after a win (else the same
   * tier again), Race Week's road again, the Odyssey's next map or next attempt; null when there is none (back to the hub).
   */
  nextRun(modifiers: LevelModifiers, won: boolean, now: number): EventRun | null {
    const e = modifiers.event;
    if (!e) return null;
    if (e.mode === "boss") return (won ? this.bossRun(now, e.step + 1) : null) ?? this.bossRun(now, e.step);
    if (e.mode === "race") return this.raceRun(now);
    return this.odysseyRun(now);
  }

  /**
   * A run ended: what it pays (each prize once a week) and, for Race Week, its time and medal (announced once on the
   * game events, `raceFinished`). Safe to call again for the same run: nothing pays or counts twice.
   */
  finishRun(r: FinishedEventRun): EventModeOutcome | null {
    const e = r.modifiers.event, d = this.data;
    if (!e || !d) return null;
    this.week(r.now);                                     // a new week's first look rolls the state before the run counts
    const base = { mode: e.mode, step: e.step, won: r.won, ms: r.ms, medal: "" as Medal | "", best: 0, newBest: false, over: false, lives: 0 };
    const owed: { prize: ModePrize; n: number; label: string }[] = [];
    let counted = false;
    let out = base;
    if (e.mode === "boss") {
      const rec = this.state.recordBoss(e.week, e.step, r.won, r.key);
      counted = rec.counted;
      if (rec.cleared) owed.push({ prize: d.boss.tiers[e.step - 1], n: e.step, label: e.step === d.boss.tiers.length ? "Elite tier" : `Tier ${e.step}` });
    } else if (e.mode === "race") {
      const road = raceRoad(this.catalog.balance, e.week, this.anchor()).road, medal = r.won ? raceMedal(road, r.ms) : "";
      const rec = this.state.recordRace(e.week, r.won, r.ms, medal, r.key);
      counted = rec.counted;
      for (const m of rec.pay) owed.push({ prize: d.race.medals[m], n: MEDAL_BASE + MEDALS.indexOf(m), label: MEDAL_LABEL[m] });
      out = { ...base, medal, best: rec.best, newBest: rec.newBest };
      if (rec.counted && r.won) this.events.emit("raceFinished", { week: isoWeekKey(e.week), level: r.level, ms: r.ms, medal, key: r.key, at: r.now });
    } else {
      const rec = this.state.recordOdyssey(e.week, e.step, r.won, r.baseHp, d.odyssey.maps.length, r.key);
      counted = rec.counted;
      if (rec.mapPaid) owed.push({ prize: d.odyssey.maps[e.step - 1], n: MAP_BASE + e.step - 1, label: `Map ${e.step}` });
      if (rec.fullPaid) owed.push({ prize: d.odyssey.full, n: FULL_PRIZE, label: "Odyssey complete" });
      out = { ...base, over: rec.over, lives: rec.lives };
    }
    const event = rotationWeek(this.catalog.balance.events, e.week);
    const prizes = owed.map((o) => this.pay(event, o.prize, o.n, o.label, r.now));
    return { ...out, counted, prizes, embers: prizes.reduce((n, p) => n + p.embers, 0), event };
  }

  private pay(event: LiveEventData | null, prize: ModePrize, n: number, label: string, now: number): PrizePaid {
    const c = this.catalog, id = event?.id ?? `week${this.state.week}`, ref = `${id}.mode${n}`;
    const paid = payReward(c.balance, prize.reward, { profile: c.profile, wallet: c.economy.wallet, level: c.highestLevelCleared, seed: metaSeed(c.playerId, `${PRIZE_TAG}:${id}`, n), at: now, ref });
    const embers = event && prize.embers > 0 ? c.endgame.events.creditBattle(event, ref, prize.embers) : 0;
    return { label, paid, gear: paid.chest ? c.bankChest(paid.chest) : [], embers };
  }
}

/** "2026-W43": the ISO week key of the week starting on Monday `monday` (RaceFinished.week). */
export function isoWeekKey(monday: number): string {
  const [y, w] = isoWeek(monday);
  return `${y}-W${w < 10 ? "0" : ""}${w}`;
}
